import React, { useState, useMemo } from 'react';
import { PlusIcon, BriefcaseIcon, Pill, FileTextIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { LineItemRow } from './LineItemRow';
import { ServicePicker } from './ServicePicker';
import { DiscountInput } from './DiscountInput';
import { useProducts } from '../../hooks/useInventory';
import type {
  PaymentLineItemFormData,
  PaymentLineItemWithCalculations,
  DiscountType,
} from '../../types/services';
import {
  calculateLineItemNetAmount,
  calculatePaymentTotals,
  formatCurrency,
} from '../../types/services';
import type { Service } from '../../types/services';

interface LineItemBuilderProps {
  lineItems: PaymentLineItemFormData[];
  overallDiscountType: DiscountType | null | undefined;
  overallDiscountValue: number | undefined;
  overallDiscountReason: string | null | undefined;
  taxEnabled: boolean;
  taxRate: number;
  onLineItemsChange: (items: PaymentLineItemFormData[]) => void;
  onOverallDiscountChange: (
    type: DiscountType | null | undefined,
    value: number | undefined,
    reason: string | null | undefined
  ) => void;
  onTaxEnabledChange: (enabled: boolean) => void;
  onTaxRateChange: (rate: number) => void;
}

let itemIdCounter = 0;
function generateItemId(): string {
  return `temp-${++itemIdCounter}-${Date.now()}`;
}

export function LineItemBuilder({
  lineItems,
  overallDiscountType,
  overallDiscountValue,
  overallDiscountReason,
  taxEnabled,
  taxRate,
  onLineItemsChange,
  onOverallDiscountChange,
  onTaxEnabledChange,
  onTaxRateChange,
}: LineItemBuilderProps) {
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [showSupplementPicker, setShowSupplementPicker] = useState(false);
  const [showOverallDiscount, setShowOverallDiscount] = useState(
    Boolean(overallDiscountType && overallDiscountValue)
  );

  const { data: productsData } = useProducts({ is_active: true });
  const products = productsData?.products ?? [];

  // Calculate line items with calculations
  const itemsWithCalculations: PaymentLineItemWithCalculations[] = useMemo(() => {
    return lineItems.map((item) => {
      const { grossAmount, discountAmount, netAmount } = calculateLineItemNetAmount(
        item.quantity,
        item.unit_price,
        item.discount_type,
        item.discount_value
      );
      return {
        ...item,
        gross_amount: grossAmount,
        discount_amount: discountAmount,
        net_amount: netAmount,
      };
    });
  }, [lineItems]);

  // Calculate totals
  const totals = useMemo(() => {
    return calculatePaymentTotals(
      itemsWithCalculations,
      overallDiscountType,
      overallDiscountValue,
      taxEnabled,
      taxRate
    );
  }, [itemsWithCalculations, overallDiscountType, overallDiscountValue, taxEnabled, taxRate]);

  const addLineItem = (item: PaymentLineItemFormData) => {
    onLineItemsChange([...lineItems, { ...item, id: generateItemId() }]);
  };

  const updateLineItem = (index: number, updates: Partial<PaymentLineItemFormData>) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], ...updates };
    onLineItemsChange(newItems);
  };

  const removeLineItem = (index: number) => {
    onLineItemsChange(lineItems.filter((_, i) => i !== index));
  };

  const handleServiceSelect = (service: Service) => {
    addLineItem({
      item_type: 'service',
      service_id: service.id,
      description: service.name,
      quantity: 1,
      unit_price: service.default_price ?? 0,
    });
  };

  const handleAddCustomItem = () => {
    addLineItem({
      item_type: 'other',
      description: '',
      quantity: 1,
      unit_price: 0,
    });
  };

  return (
    <div className="space-y-4">
      {/* Line Items List */}
      <div className="space-y-3">
        {lineItems.length === 0 ? (
          <div className="py-8 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-500 mb-4">No items added yet</p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => setShowServicePicker(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#C9A870] bg-[#C9A870]/10 rounded-lg hover:bg-[#C9A870]/20 transition-colors"
              >
                <BriefcaseIcon className="w-4 h-4" />
                Add Service
              </button>
              <button
                type="button"
                onClick={handleAddCustomItem}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <FileTextIcon className="w-4 h-4" />
                Custom Item
              </button>
            </div>
          </div>
        ) : (
          <>
            {lineItems.map((item, index) => (
              <LineItemRow
                key={item.id || index}
                item={item}
                index={index}
                onUpdate={(updates) => updateLineItem(index, updates)}
                onRemove={() => removeLineItem(index)}
              />
            ))}

            {/* Add more items buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowServicePicker(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#C9A870] border border-[#C9A870]/30 rounded-lg hover:bg-[#C9A870]/10 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Service
              </button>
              <button
                type="button"
                onClick={handleAddCustomItem}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Custom
              </button>
            </div>
          </>
        )}
      </div>

      {/* Totals Section */}
      {lineItems.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          {/* Subtotal */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
          </div>

          {/* Overall Discount Toggle */}
          <div>
            <button
              type="button"
              onClick={() => {
                if (showOverallDiscount) {
                  onOverallDiscountChange(null, undefined, null);
                }
                setShowOverallDiscount(!showOverallDiscount);
              }}
              className="flex items-center gap-1 text-sm text-[#5B8E87] hover:text-[#4F7A74] transition-colors"
            >
              {showOverallDiscount ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
              {showOverallDiscount ? 'Remove overall discount' : 'Add overall discount'}
            </button>

            {showOverallDiscount && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <DiscountInput
                  discountType={overallDiscountType}
                  discountValue={overallDiscountValue}
                  discountReason={overallDiscountReason}
                  maxAmount={totals.subtotal}
                  onDiscountTypeChange={(type) =>
                    onOverallDiscountChange(type, overallDiscountValue, overallDiscountReason)
                  }
                  onDiscountValueChange={(value) =>
                    onOverallDiscountChange(overallDiscountType, value, overallDiscountReason)
                  }
                  onDiscountReasonChange={(reason) =>
                    onOverallDiscountChange(overallDiscountType, overallDiscountValue, reason)
                  }
                  showReason
                />
              </div>
            )}
          </div>

          {/* Overall Discount Display */}
          {totals.overallDiscountAmount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Overall Discount
                {overallDiscountReason && (
                  <span className="text-gray-400 ml-1">({overallDiscountReason})</span>
                )}
              </span>
              <span className="font-medium text-red-500">
                -{formatCurrency(totals.overallDiscountAmount)}
              </span>
            </div>
          )}

          {/* Tax Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onTaxEnabledChange(!taxEnabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  taxEnabled ? 'bg-[#5B8E87]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    taxEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">Include Tax</span>
              {taxEnabled && (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => onTaxRateChange(Math.max(0, parseFloat(e.target.value) || 0))}
                    min="0"
                    max="100"
                    step="0.5"
                    className="w-14 px-2 py-1 text-sm rounded-lg border border-gray-300 focus:border-[#5B8E87] outline-none text-center"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              )}
            </div>
            {taxEnabled && (
              <span className="font-medium text-sm">+{formatCurrency(totals.taxAmount)}</span>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-900">Total</span>
              <span className="text-xl font-bold text-[#5B8E87]">
                {formatCurrency(totals.grandTotal)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Service Picker Modal */}
      <ServicePicker
        isOpen={showServicePicker}
        onClose={() => setShowServicePicker(false)}
        onSelect={handleServiceSelect}
      />
    </div>
  );
}
